import BeaconAxiosInstance from "@/api/beaconAxios";
import { getClientId } from "@/utils/helper";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BEACON_PHASES,
  type BeaconLevel,
  type BeaconPhaseKey,
  type BeaconPhaseUiState,
  type BeaconRunStatus,
  type BeaconSseEvent,
} from "@/types/beacon.types";

interface BeaconState {
  status: BeaconRunStatus;
  runId: string | null;
  sseToken: string | null;
  sseUrl: string | null;
  phases: BeaconPhaseUiState[];
  logTail: string[];
  reportSlug: string | null;
  publicUrl: string | null;
  score: { total: number; level: BeaconLevel } | null;
  inputUrl: string;
  error: string | null;
  discovery: {
    urlCount: number;
    source:
      | "sitemap"
      | "sitemap-index"
      | "wp-rest"
      | "nav-regex"
      | "crawl-fallback";
  } | null;
  standardisation: {
    branch: "A" | "B" | "C";
    sampleUrls: string[];
    rationale: string;
    locationCount: number;
    etaSeconds: number;
  } | null;
  emailHandoff: { requested: boolean; dismissed: boolean };
  // Tracks an in-flight cancel POST. Backend handles `running` cancels
  // gracefully (flag is set; runner observes at next phase boundary), so
  // there's a gap between "cancel requested" and the SSE-delivered
  // terminal `run.error`. This flag drives the "Canceling…" pill in the
  // header during that gap.
  canceling: boolean;
}

const initialPhases = (): BeaconPhaseUiState[] =>
  BEACON_PHASES.map((p) => ({
    key: p.key,
    label: p.label,
    status: "pending",
    detail: "",
  }));

const initialState: BeaconState = {
  status: "idle",
  runId: null,
  sseToken: null,
  sseUrl: null,
  phases: initialPhases(),
  logTail: [],
  reportSlug: null,
  publicUrl: null,
  score: null,
  inputUrl: "",
  error: null,
  discovery: null,
  standardisation: null,
  emailHandoff: { requested: false, dismissed: false },
  canceling: false,
};

export const startBeaconRun = createAsyncThunk(
  "beacon/start",
  async (url: string, { rejectWithValue }) => {
    try {
      // Tag the run with the operator's currently-selected agency client
      // so the Beacon hub can resume the right run when the operator
      // switches between clients, and so teammates under the same
      // (operatorId, agencyClientId) see this run in their history.
      // The client _id lives in encrypted localStorage (`getClientId()`)
      // — the Redux `selectedClientProfile` string is actually a
      // profile-picture URL used as a change trigger, not the id itself.
      //
      // Post-AN-1541 we no longer send `userId` here: the standalone
      // backend derives `triggeredBy.userId` from the JWT subject for
      // provenance, and visibility is now scoped at the client/account
      // level so the per-user write-tag is no longer load-bearing for
      // read isolation.
      const agencyClientId = getClientId() || null;
      const res = await BeaconAxiosInstance.post("/beacon/runs", {
        url,
        agencyClientId,
      });
      const data = res.data?.data ?? res.data;
      return {
        runId: data.runId,
        sseToken: data.sseToken,
        sseUrl: data.sseUrl,
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to start run",
      );
    }
  },
);

// Re-attach to a still-running audit on a fresh page load. The original
// SSE token signed at startRun expires after 30 minutes; this re-mints
// one and slots the run details into the slice so RunProgress's
// `useEffect([sseUrl, status])` picks up and reconnects. The SSE replay
// buffer on the server delivers every event published before subscribe,
// so the UI reconstructs full progress.
export const resumeBeaconRun = createAsyncThunk(
  "beacon/resume",
  async (
    { runId, inputUrl }: { runId: string; inputUrl: string },
    { rejectWithValue },
  ) => {
    try {
      // Post-AN-1541 the SSE-token mint accepts an empty body. Visibility
      // is scoped by (operatorId, agencyClientId), not by run-owner, so a
      // teammate can resume a colleague's in-flight stream once the
      // original 30-min token expires. The standalone backend still
      // binds the new token to the calling user's JWT subject for
      // replay-buffer accounting; that's a delivery concern, not an
      // auth one, and is derived server-side.
      const res = await BeaconAxiosInstance.post(
        `/beacon/runs/${runId}/sse-token`,
        {},
      );
      const data = res.data?.data ?? res.data;
      return {
        runId: data.runId ?? runId,
        sseToken: data.sseToken,
        sseUrl: data.sseUrl,
        inputUrl,
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to resume run",
      );
    }
  },
);

export const cancelRun = createAsyncThunk(
  "beacon/cancel",
  async (runId: string, { rejectWithValue }) => {
    try {
      await BeaconAxiosInstance.post(`/beacon/runs/${runId}/cancel`);
      return;
    } catch (err: any) {
      // 409 means the run already terminated (race with the SSE stream
      // delivering the terminal event). Treat it as success — there's
      // nothing to cancel and the UI will update via SSE momentarily.
      if (err?.response?.status === 409) return;
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to cancel run",
      );
    }
  },
);

export const requestEmailHandoff = createAsyncThunk(
  "beacon/handoff",
  async (
    { runId, email }: { runId: string; email?: string },
    { rejectWithValue },
  ) => {
    try {
      // Email is optional now — the user is signed in, so the backend
      // resolves the recipient from their session. We still accept an
      // explicit override for tests / future unauth flows.
      await BeaconAxiosInstance.post(
        `/beacon/runs/${runId}/handoff`,
        email ? { email } : {},
      );
      return { email };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message ?? err?.message ?? "Failed to queue email",
      );
    }
  },
);

const beaconSlice = createSlice({
  name: "beacon",
  initialState,
  reducers: {
    setInputUrl(state, action: PayloadAction<string>) {
      state.inputUrl = action.payload;
    },
    enterConfirming(state) {
      state.status = "confirming";
      state.error = null;
    },
    editUrl(state) {
      state.status = "idle";
      state.error = null;
    },
    /** Dev/preview-only: force status without hitting the backend. */
    __devSetStatus(state, action: PayloadAction<BeaconRunStatus>) {
      state.status = action.payload;
    },
    /**
     * Attach to a Beacon run that was started elsewhere (e.g. speculatively
     * from the AEO query tool). Replays via SSE then stream forward — the
     * backend's replay buffer means we don't miss phase events that fired
     * before this tab opened the EventSource.
     */
    attachToRun(
      state,
      action: PayloadAction<{
        runId: string;
        sseToken: string;
        sseUrl: string;
        inputUrl?: string;
      }>,
    ) {
      state.status = "running";
      state.runId = action.payload.runId;
      state.sseToken = action.payload.sseToken;
      state.sseUrl = action.payload.sseUrl;
      if (typeof action.payload.inputUrl === "string") {
        state.inputUrl = action.payload.inputUrl;
      }
      state.phases = initialPhases();
      state.logTail = [];
      state.reportSlug = null;
      state.publicUrl = null;
      state.score = null;
      state.error = null;
      state.discovery = null;
      state.standardisation = null;
      state.emailHandoff = { requested: false, dismissed: false };
    },
    handleSseEvent(state, action: PayloadAction<BeaconSseEvent>) {
      const ev = action.payload;
      switch (ev.type) {
        case "phase.start": {
          const p = state.phases.find((x) => x.key === ev.key);
          if (p) p.status = "running";
          break;
        }
        case "phase.progress": {
          const p = state.phases.find((x) => x.key === ev.key);
          if (p) {
            p.detail = ev.detail;
            if (ev.progress) p.progress = ev.progress;
          }
          state.logTail.push(`[${shortKey(ev.key)}] ${ev.detail}`);
          if (state.logTail.length > 80) state.logTail.shift();
          break;
        }
        case "phase.end": {
          const p = state.phases.find((x) => x.key === ev.key);
          if (p) {
            p.status = ev.status;
            p.durationMs = ev.durationMs;
          }
          break;
        }
        case "run.log": {
          state.logTail.push(`[${ev.level}] ${ev.message}`);
          if (state.logTail.length > 80) state.logTail.shift();
          break;
        }
        case "discovery.complete": {
          state.discovery = { urlCount: ev.urlCount, source: ev.source };
          state.logTail.push(
            `[discovery] mapped ${ev.urlCount} URLs via ${ev.source}`,
          );
          if (state.logTail.length > 80) state.logTail.shift();
          break;
        }
        case "standardisation.complete": {
          state.standardisation = {
            branch: ev.branch,
            sampleUrls: ev.sampleUrls,
            rationale: ev.rationale,
            locationCount: ev.locationCount,
            etaSeconds: ev.etaSeconds,
          };
          state.logTail.push(
            `[standardisation] branch ${ev.branch} · ${ev.locationCount} locations · ETA ${ev.etaSeconds}s`,
          );
          if (state.logTail.length > 80) state.logTail.shift();
          break;
        }
        case "run.complete": {
          state.status = "succeeded";
          state.reportSlug = ev.reportSlug;
          state.publicUrl = ev.publicUrl;
          state.score = ev.score;
          state.canceling = false;
          state.phases.forEach((p) => {
            if (p.status === "pending" || p.status === "running") p.status = "ok";
          });
          break;
        }
        case "run.error": {
          state.status = "failed";
          state.error = ev.message;
          state.canceling = false;
          const p = ev.phase ? state.phases.find((x) => x.key === ev.phase) : null;
          if (p) p.status = "fail";
          break;
        }
      }
    },
    resetRun(state) {
      state.status = "idle";
      state.runId = null;
      state.sseToken = null;
      state.sseUrl = null;
      state.phases = initialPhases();
      state.logTail = [];
      state.reportSlug = null;
      state.publicUrl = null;
      state.score = null;
      state.error = null;
      state.discovery = null;
      state.standardisation = null;
      state.emailHandoff = { requested: false, dismissed: false };
      state.canceling = false;
    },
    dismissEmailHandoff(state) {
      state.emailHandoff.dismissed = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startBeaconRun.pending, (state) => {
        state.status = "queued";
        state.runId = null;
        state.sseToken = null;
        state.sseUrl = null;
        state.phases = initialPhases();
        state.logTail = [];
        state.reportSlug = null;
        state.publicUrl = null;
        state.score = null;
        state.error = null;
        state.discovery = null;
        state.standardisation = null;
        state.canceling = false;
      })
      .addCase(startBeaconRun.fulfilled, (state, action) => {
        state.status = "running";
        state.runId = action.payload.runId;
        state.sseToken = action.payload.sseToken;
        state.sseUrl = action.payload.sseUrl;
      })
      .addCase(startBeaconRun.rejected, (state, action) => {
        state.status = "failed";
        state.error = (action.payload as string) ?? "Failed to start run";
      })
      .addCase(resumeBeaconRun.fulfilled, (state, action) => {
        // Mirror the post-start shape: status `running` so RunProgress's
        // SSE effect attaches; SSE replay buffer delivers prior events.
        state.status = "running";
        state.runId = action.payload.runId;
        state.sseToken = action.payload.sseToken;
        state.sseUrl = action.payload.sseUrl;
        state.inputUrl = action.payload.inputUrl;
        state.phases = initialPhases();
        state.logTail = [];
        state.reportSlug = null;
        state.publicUrl = null;
        state.score = null;
        state.error = null;
        state.discovery = null;
        state.standardisation = null;
        state.canceling = false;
      })
      .addCase(requestEmailHandoff.fulfilled, (state) => {
        state.emailHandoff.requested = true;
      })
      .addCase(cancelRun.pending, (state) => {
        state.canceling = true;
      })
      .addCase(cancelRun.fulfilled, (state) => {
        // Don't flip status here — the SSE stream will deliver the
        // terminal run.error. Keep `canceling` true until then so the
        // "Canceling…" pill stays visible.
      })
      .addCase(cancelRun.rejected, (state) => {
        state.canceling = false;
      });
  },
});

function shortKey(k: BeaconPhaseKey): string {
  return k.replace(/\./g, ":");
}

export const {
  setInputUrl,
  enterConfirming,
  editUrl,
  handleSseEvent,
  resetRun,
  dismissEmailHandoff,
  __devSetStatus,
  attachToRun,
} = beaconSlice.actions;
export default beaconSlice.reducer;
